import { Box, emphasize, Grid, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useReducer } from "react";
import { MonthCalendar, WeekCalendar } from "../../components/Calendar";
import { ChartLabels, DonutChart } from "../../components/Charts";
import { initPeriod } from "../../components/Period";

const reducer = (state, action) => {
    if (action.type === "SET_FILTER") {
        return {
            ...state,
            filter: action.filter,
        };
    }

    return state;
};

const initState = (initialState) => {
    return initialState ?? {};
};

export const useTimesheetState = (employeeId, periodType, date) => {
    const [state, dispatch] = useReducer(reducer, {}, initState);

    const useEffect = (() => {}, [dispatch, periodType, date, employeeId]);
    const refetchData = async () => {};
    const setFilter = async (filter) => {
        dispatch({ type: "SET_FILTER", filter });
    };

    return {
        state: state.data || {},
        filter: state.filter || {},
        refetchData,
        setFilter,
    };
};

const MonthDay = ({
    children,
    onClick,
    day,
    dayNumber,
    onAction,
    actionIcon,
    actionTitle,
    maxThreshold,
    theme,
    value,
    onDayClick,
}) => {
    let color = theme.palette.surfaceContainerHighest?.main;
    const val = value;

    if (!isNaN(val)) {
        if (isNaN(maxThreshold) || val > maxThreshold) {
            color = theme.palette.primary.main;
        } else if (maxThreshold) {
            color = emphasize(theme.palette.primary.main, (1 - Math.abs(val / maxThreshold)) * 0.8);
        }
    }

    return (
        <Box
            sx={(theme) => ({
                paddingTop: "100%",
                cursor: onDayClick ? "pointer" : "default",
                color: theme.palette.getContrastText(color),
                backgroundColor: color,
                "& > div": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                },
                "& > .MonthDayAction": {
                    display: "none",
                },
                "&:hover > .MonthDayAction": {
                    display: "block",
                },
            })}
            onClick={() => onDayClick?.(day)}>
            <Typography component="div">{dayNumber}</Typography>
            {children}
        </Box>
    );
};

const TimesheetCalendar = ({ period, employeeId, data, theme, onChangePeriod }) => {
    const { type, date } = period ?? {};
    const d = dayjs(date);
    if (type === "month") {
        return (
            <MonthCalendar
                year={d.year()}
                month={d.month()}
                data={data}
                renderDay={(day) => (
                    <MonthDay
                        day={day.date}
                        dayNumber={day.dayNumber}
                        onDayClick={(day) => {
                            onChangePeriod?.(initPeriod("day", day));
                        }}
                        theme={theme}
                        value={data?.[day.date]?.value}
                        maxThreshold={8}
                    />
                )}
            />
        );
    }
    if (type === "isoWeek") {
        return (
            <WeekCalendar
                year={d.year()}
                week={d.isoWeek()}
                data={data}
                maxThreshold={8}
                onDayClick={(day) => {
                    onChangePeriod?.(initPeriod("day", day));
                }}
            />
        );
    }
    if (type === "day") {
        return null;
    }
    return null;
};

export const TimesheetOverview = ({ employeeId, period, data, onChangePeriod }) => {
    const theme = useTheme();
    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={12} xl={6}>
                <TimesheetCalendar
                    period={period}
                    employeeId={employeeId}
                    data={data?.calendar}
                    theme={theme}
                    onChangePeriod={onChangePeriod}
                />
            </Grid>
            <Grid item xs={12} md={6} lg={12} xl={6}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 4,
                    }}>
                    <Box>
                        <DonutChart
                            data={[data?.projects?.map((p) => p.value) || []]}
                            labels={[]}
                            colors={[theme.palette.primary.main, theme.palette.secondary.main]}
                            centerTitle="Celkem hodin"
                            centerValue={`${data?.hours || 0} h`}></DonutChart>
                    </Box>
                    {Array.isArray(data?.projects) && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-around",
                            }}>
                            <Box sx={{ padding: 8 }}>
                                <ChartLabels
                                    labels={data.projects}
                                    color={theme.palette.primary.main}
                                />
                            </Box>
                            <Box sx={{ padding: 8 }}>
                                <ChartLabels
                                    labels={data.projects}
                                    color={theme.palette.primary.main}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};
